// MACHINERY V2 (Stroje) — one catalog model card (M2.8).
//
// Spec: data package §11 field table ("Current UI" column) + its Conditional
// states. Reference impl: design_handoff_machinery_v3/machinery-catalog.jsx
// (`CatalogCard`); artboards 0900 (refined-claude) / 0901 (machineid-native) —
// SAME layout, both palettes, which is why nothing here carries a fixed hex
// except the pinned readiness signal colours the M2.9 kit owns.
//
// >>> THE WHOLE CARD IS CLICKABLE, SO EVERY EMBEDDED CONTROL SWALLOWS. <<<
// This is the exact shape that bit Wave 2: a `CapabilityBadge` inside a
// clickable fleet card bubbled its click into card navigation, and it was a
// Codex-only catch AFTER /code-review had marked the guards clean. The card
// carries THREE interactive children — two capability badges and the Write-
// gated add button — over a navigating host.
//   · The badges swallow inside CapabilityBadge itself — its onClick AND its
//     onKeyDown both call stopPropagation, interactive or not (that file's "THE
//     HANDLER IS ALWAYS ATTACHED" note). Nothing extra is needed here. The
//     keyboard half was added at M2.8 BECAUSE of this card: it is the first v2
//     card with an onKeyDown of its own (FleetCard has none), so it is where a
//     badge keypress first had a navigating handler to bubble into.
//   · The add button swallows BOTH activation paths — mouse AND keyboard — per
//     the Wave-4 C4 upgrade. A keyboard user pressing Enter on the button gets
//     a click event that bubbles just like a mouse click does.
//   · The POPUP is not mounted here at all. It is a page-level sibling
//     (CatalogBrowseV2), so it is not even a React ancestor of the card and a
//     click on its backdrop/Close/CTA cannot reach this onClick. Same reasoning
//     as FleetPage.tsx:276-290.
//
// >>> MAX ONE RIBBON — AND THIS FILE DOES NOT DECIDE WHICH. <<<
// `ReadinessBanner` reads `presentation.banner`, a single value from
// `bannerTier()` (gold OEM outranks AI-blue), and renders nothing when it is
// null. There is no precedence logic here, no second opinion on
// `isOemPartner`, and no `readiness &&` guard: absence is an unlit badge, never
// a hidden one (M2.9, locked).
//
// The card is a <div role="button">, not a <button>: it contains a button and
// nesting interactive elements is invalid HTML that browsers reflow
// unpredictably. Keyboard activation is wired explicitly instead.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { CatalogModelListItem } from "../../models/models";
import { CapabilityBadges } from "../readiness/CapabilityBadge";
import ReadinessBanner from "../readiness/ReadinessBanner";
import { MachineryV2ReadinessPresentation, RD } from "../readiness/readinessPresentation";
import { CatalogKeySpecs, CatalogThumb, CategoryBadge, catalogThumbUrl } from "./catalogAtoms";

export interface CatalogModelCardV2Props {
  model: CatalogModelListItem;
  /** Derived ONCE by the page via `deriveReadinessPresentation` — never here. */
  presentation: MachineryV2ReadinessPresentation;
  onOpen: (model: CatalogModelListItem) => void;
  onOpenReadiness: (model: CatalogModelListItem) => void;
  /** usePermission("Machinery").canWrite — §11 gates the add affordance. */
  canWrite: boolean;
  onAdd: (model: CatalogModelListItem) => void;
}

const CatalogModelCardV2: React.FC<CatalogModelCardV2Props> = ({
  model,
  presentation,
  onOpen,
  onOpenReadiness,
  canWrite,
  onAdd,
}) => {
  const { translate } = useTranslation("myMachinery");

  const gold = presentation.banner === "oem-gold";
  const blue = presentation.banner === "ai-blue";
  const units = model.unitsOnPlatform || 0;

  const manufacturerLine = model.manufacturer
    ? formatManufacturerWithHierarchy(
        model.manufacturer.name,
        model.manufacturer.parentManufacturerName
      )
    : "";

  const activateAdd = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onAdd(model);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(model)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(model);
        }
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        borderRadius: 10,
        overflow: "hidden",
        cursor: "pointer",
        background: "var(--card)",
        // The ribbon's own hairline, from the SAME pinned cross-palette bank
        // ReadinessBanner paints with — never a second copy of the hex.
        border:
          "1px solid " + (gold ? RD.goldLine : blue ? RD.blueLine : "var(--border)"),
        boxShadow: "var(--shadow)",
      }}
    >
      <ReadinessBanner presentation={presentation} oemPartnerName={model.oemPartnerName} />

      <div style={{ padding: "11px 13px 0", display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
        <CategoryBadge code={model.categoryCode} />
        <span style={{ flex: 1 }} />
        {units > 0 && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 20,
              padding: "0 8px",
              borderRadius: 999,
              background: "color-mix(in oklab, var(--green) 12%, var(--card))",
              border: "1px solid color-mix(in oklab, var(--green) 30%, var(--border))",
              color: "var(--green)",
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {translate("myMachinery:v2.catalog.unitsHere", { count: units })}
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 11, padding: "10px 13px", minWidth: 0 }}>
        <CatalogThumb url={catalogThumbUrl(model)} alt={model.modelName} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <div
            title={model.modelName}
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--ink)",
              letterSpacing: "-0.01em",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {model.modelName}
          </div>
          {manufacturerLine && (
            <div
              title={manufacturerLine}
              style={{
                fontSize: 12,
                color: "var(--muted)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {manufacturerLine}
            </div>
          )}
          {model.subType && (
            <div
              style={{
                fontSize: 11.5,
                color: "var(--faint)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {model.subType}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "0 13px 11px", minWidth: 0 }}>
        <CatalogKeySpecs model={model} />
      </div>

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "9px 13px",
          borderTop: "1px solid var(--border-soft)",
          background: "var(--bg-soft)",
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        {/* The readiness surface. Both badges always render; the popup opens
            from them, and every commercial CTA lives inside it. */}
        <CapabilityBadges
          presentation={presentation}
          size={20}
          onOpenDetails={() => onOpenReadiness(model)}
        />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            color: presentation.hasAnyDocs ? "var(--muted)" : "var(--faint)",
            whiteSpace: "nowrap",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {/* The util's own doc phrase — same source as the badge tooltip and
              the popup, so the three can never disagree. */}
          {presentation.summary.docPart || translate("myMachinery:midasNoDocsYet")}
        </span>

        <span style={{ flex: 1 }} />

        <span
          aria-hidden
          style={{ fontSize: 11.5, color: "var(--mod-accent)", whiteSpace: "nowrap" }}
        >
          {translate("myMachinery:viewDetails")} ›
        </span>

        {canWrite && (
          <button
            type="button"
            className="btn btn-mod"
            onClick={activateAdd}
            onKeyDown={(e) => {
              // Enter/Space on a focused <button> fires a click that bubbles to
              // the card exactly like a mouse click. Swallow the key too.
              if (e.key === "Enter" || e.key === " ") e.stopPropagation();
            }}
            style={{ height: 28, fontSize: 11.5, whiteSpace: "nowrap", flexShrink: 0 }}
          >
            <span aria-hidden style={{ marginRight: 3 }}>
              +
            </span>
            {translate("myMachinery:addMachine")}
          </button>
        )}
      </div>
    </div>
  );
};

export default CatalogModelCardV2;
